<template>
  <ActivityFeed
    :is-loading="isLoading"
    :feed-item-list="feedItemList"
    :height="height"
    :pagination-reset-key="paginationResetKey"
    @click:table-row="showAbortImageModal"
  >
    <template #tableContent="{ content }">
      <div
        v-if="isAppointmentItem(content)"
        class="ml-4 mt-4 mb-2 d-flex flex-column ga-1"
      >
        <SoLink
          v-if="content.notes.length"
          icon="chevron-right"
          text="Engineer Notes"
          @click="setNotes(content)"
        />
        <SoLink
          v-if="content.surveyImages.length"
          icon="chevron-right"
          :text="photoCountText(content.surveyImages)"
          data-testid="survey-button"
          @click="openSurveyModal(content.surveyImages)"
        />
      </div>
    </template>
  </ActivityFeed>
  <SoDialog
    ref="engineer-notes-modal"
    v-model="showNotesModal"
    card-title="Engineer Notes"
    @on-close="closeNotesModal"
  >
    <div v-if="comments.items.length" class="mb-4">
      <h5 class="mb-2">Comments</h5>
      <SoSimpleTable
        :headers="comments.headers"
        :items="[comments.items]"
        is-pivoted
        equal-width-columns
      />
    </div>
    <div v-if="notes.items.length">
      <h5 class="mb-2">Survey Questions</h5>
      <SoSimpleTable
        :headers="notes.headers"
        :items="[notes.items]"
        is-pivoted
        equal-width-columns
      />
    </div>
  </SoDialog>
  <SoDialog
    ref="abort-image-modal"
    v-model="abortImageModalOpen"
    :card-title="abortImageModalContent.title"
  >
    <SoCarousel
      v-if="Object.keys(abortImageModalContent.image).length"
      ref="abort-image-carousel"
      :images="[abortImageModalContent.image]"
    />
  </SoDialog>
  <SurveyModal
    v-model="surveyModalOpen"
    :survey-images="currentSurveyImages"
    @close-modal="closeSurveyModal"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import ActivityFeed from "./ActivityFeed.vue"
  import {
    IMeterAppointmentSurvey,
    IMeterAppointmentComment,
  } from "@/models/meterAppointment/meterAppointment.types"
  import { MeterAppointment } from "@/models/meterAppointment/meterAppointment"
  import { IItemsByDate } from "@/models/activityFeed/ActivityFeedItem"
  import SoCarousel, { SoCarouselItem } from "@/components/SoCarousel.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import {
    SoSimpleTableHeaders,
    SoSimpleTableRow,
  } from "@/types/components/SoSimpleTable"
  import SoLink from "@/components/SoLink.vue"
  import SurveyModal from "../../smartDevices/modals/SurveyModal.vue"
  import { formatDateTime } from "@/helpers/dateHelper"

  export default defineComponent({
    name: "MeterAppointmentSlot",
    components: {
      ActivityFeed,
      SoDialog,
      SoSimpleTable,
      SoLink,
      SoCarousel,
      SurveyModal,
    },
    props: {
      feedItemList: {
        type: Array as PropType<IItemsByDate[]>,
        default: () => [],
        required: true,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      height: {
        type: String,
        default: "600px",
      },
      paginationResetKey: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        showNotesModal: false,
        abortImageModalOpen: false,
        abortImageModalContent: {
          image: {} as SoCarouselItem,
          title: "",
        },
        notes: {
          headers: [] as SoSimpleTableHeaders,
          items: [] as SoSimpleTableRow,
        },
        comments: {
          headers: [] as SoSimpleTableHeaders,
          items: [] as SoSimpleTableRow,
        },
        surveyModalOpen: false,
        currentSurveyImages: [] as SoCarouselItem[],
      }
    },
    methods: {
      isAppointmentItem(content: unknown): content is MeterAppointment {
        return (
          content !== null &&
          typeof content === "object" &&
          Array.isArray((content as Partial<MeterAppointment>).notes) &&
          Array.isArray((content as Partial<MeterAppointment>).surveyImages)
        )
      },
      setNotes(source: {
        notes?: IMeterAppointmentSurvey[]
        comments?: IMeterAppointmentComment[]
      }) {
        const { notes = [], comments = [] } = source
        notes.forEach((note) => {
          this.notes.headers.push(note.question)
          this.notes.items.push(note.answer)
        })

        comments.forEach((comment) => {
          this.comments.headers.push(
            comment.createdAt ? formatDateTime(dayjs(comment.createdAt)) : "",
          )
          this.comments.items.push(comment.comment)
        })

        this.showNotesModal = true
      },
      closeNotesModal() {
        this.notes = { headers: [], items: [] }
        this.comments = { headers: [], items: [] }
      },
      showAbortImageModal(item: unknown) {
        if (!this.isAppointmentItem(item)) return
        const imageSrc = item.abortImageFromSurvey
        if (imageSrc) {
          this.abortImageModalOpen = true
          this.abortImageModalContent = {
            image: { src: imageSrc },
            title: "Abort image",
          }
        }
      },
      openSurveyModal(surveyImages: SoCarouselItem[]) {
        this.currentSurveyImages = surveyImages
        this.surveyModalOpen = true
      },
      closeSurveyModal() {
        this.currentSurveyImages = []
      },
      photoCountText(images: SoCarouselItem[]) {
        return `Photos (${images.length})`
      },
    },
  })
</script>
